import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';

/**
 * Stringify a value as pretty JSON, with bigint values turned into decimal
 * strings so u64/i64 fields don't throw.
 */
function stringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) => (typeof v === 'bigint' ? v.toString() : v), 2);
}

/**
 * Render a value as a Prism-highlighted JSON tree. Returns `<pre><code>`
 * with the same `.token.*` palette as the rest of the playground's code
 * blocks, so toasts / event-page cards / future renderers all paint
 * identically to the Quickstart snippets.
 */
export function JsonTree({ value, className }: { value: unknown; className?: string }) {
  const source = stringify(value);
  const html = Prism.highlight(source, Prism.languages.json, 'json');
  return (
    <pre className={className ? `json-tree ${className}` : 'json-tree'}>
      <code
        className="language-json"
        // Prism returns sanitized HTML built from the input string; safe to
        // inject. We never accept user-authored markup as `value`.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </pre>
  );
}

/**
 * Returns Prism-highlighted JSON HTML as a React node — for callers that
 * want to embed the rendered tree inside an existing `<pre>`. Use
 * `JsonTree` when you need a standalone block.
 */
export function highlightedJson(value: unknown) {
  const source = stringify(value);
  const html = Prism.highlight(source, Prism.languages.json, 'json');
  return <code className="language-json" dangerouslySetInnerHTML={{ __html: html }} />;
}
